<template>
  <div class="container mx-auto px-11 pt-5">
    <h1 class="mb-3">
      <span v-if="form" class="text-6xl primary--text page-title">{{
        user.full_name
      }}</span>
      <v-skeleton-loader v-else class="py-6" max-width="300" type="text" />
    </h1>

    <div>
      <v-breadcrumbs
        v-if="form"
        class="text-xs pa-0"
        :items="breadCrumbItems"
        divider="-"
      >
        <template v-slot:divider>
          <v-icon small class="mt-0">{{ icons.arrow }}</v-icon>
        </template>
      </v-breadcrumbs>
      <v-skeleton-loader v-else max-width="160" type="text" />
    </div>

    <div class="mt-12 pa-0 w-3/4 d-flex justify-space-between">
      <h5 class="headline">
        <span v-if="form">User Details</span>
        <v-skeleton-loader v-else max-width="100" type="text" />
      </h5>

      <template v-if="!loading">
        <div>
          <v-menu
            :close-on-content-click="false"
            v-model="menu"
            bottom
            left
            transition="scale-transition"
            origin="top right"
            v-if="user"
            :disabled="
              user.is_verified ||
                (user.is_rejected && user.reinstated_at === null)
            "
          >
            <template v-slot:activator="{ on }">
              <v-chip
                v-if="user.is_verified"
                pill
                small
                v-on="on"
                color="success"
                text-color="white"
              >
                <v-icon left small>{{ icons.accountCheck }}</v-icon
                >Verified
              </v-chip>
              <v-chip
                v-else-if="user.rejected_at && user.reinstated_at === null"
                pill
                small
                v-on="on"
                color="red"
                text-color="white"
              >
                <v-icon left small>{{ icons.accountCancel }}</v-icon
                >Rejected
              </v-chip>
              <v-chip
                v-else-if="user.rejected_at && user.reinstated_at !== null"
                pill
                small
                v-on="on"
                color="warning"
                text-color="white"
              >
                <v-icon left small>{{ icons.accountAlert }}</v-icon
                >Reinstated
              </v-chip>
              <v-chip
                v-else
                pill
                small
                v-on="on"
                color="warning"
                text-color="white"
              >
                <v-icon left small>{{ icons.accountAlert }}</v-icon
                >Unverified
              </v-chip>
            </template>

            <v-card width="300">
              <v-list>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title v-if="user">
                      {{ user.full_name }}
                    </v-list-item-title>
                    <v-list-item-subtitle v-if="user">
                      {{ user.email }}
                    </v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action class="d-flex flex-row">
                    <v-btn icon @click="onAcceptVerification" small>
                      <v-icon color="success">{{ icons.check }}</v-icon>
                    </v-btn>

                    <v-btn icon @click="onRejectVerification" small>
                      <v-icon color="red">{{ icons.close }}</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>

              <v-divider></v-divider>

              <v-list>
                <v-list-item>
                  <v-list-item-subtitle>
                    <v-textarea
                      solo
                      auto-grow
                      v-model="form.rejection_message"
                      name="input-7-4"
                      label="Reason for rejection."
                    ></v-textarea>
                  </v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-card>
          </v-menu>
          <v-menu offset-y v-if="user.is_verified">
            <template v-slot:activator="{ on, attrs }">
              <v-btn color="grey" x-small icon v-bind="attrs" v-on="on">
                <v-icon color="grey">{{ icons.settings }}</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item link>
                <v-list-item-title @click="unverify"
                  >Unverify</v-list-item-title
                >
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </template>
      <v-skeleton-loader v-else type="chip" />
    </div>

    <div v-if="form" class="mt-8 mb-12 pa-0 w-3/4" style="margin-bottom: 50px">
      <v-form ref="form">
        <div class="flex flex-wrap">
          <div class="w-3/4 mt-5">
            <avatar-form
              :user="user"
              @change="avatarChange"
              @avatarRemove="userAvatarRemoved"
            />
          </div>
          <div class="w-1/4"></div>
          <div class="w-1/2 pr-3">
            <label>First name</label>
            <v-text-field
              flat
              solo
              required
              class="mt-2"
              background-color="grey lighten-3"
              v-model="form.first_name"
              :error-messages="form.$getError('first_name')"
            ></v-text-field>
          </div>
          <div class="w-1/2 pr-3">
            <label>Last name</label>
            <v-text-field
              flat
              solo
              required
              class="mt-2"
              v-model="form.last_name"
              background-color="grey lighten-3"
              :error-messages="form.$getError('last_name')"
            ></v-text-field>
          </div>
          <div class="w-1/2 pr-3">
            <label>Email Address</label>
            <v-text-field
              flat
              solo
              readonly
              required
              class="mt-2"
              v-model="form.email"
              background-color="grey lighten-3"
              :error-messages="form.$getError('email')"
            ></v-text-field>
          </div>

          <div class="w-1/2 pr-3">
            <label>Phone number</label>
            <v-text-field
              flat
              solo
              readonly
              required
              class="mt-2"
              v-model="form.phone_number"
              background-color="grey lighten-3"
              :error-messages="form.$getError('phone_number')"
            ></v-text-field>
          </div>
        </div>

        <v-btn
          color="primary"
          depressed
          class="mr-4"
          @click="updateUserDetails"
          :loading="form.$busy"
          >Update</v-btn
        >
      </v-form>
    </div>

    <div v-if="form" class="mt-8 mb-12 pa-0 w-3/4" style="margin-bottom: 50px">
      <v-divider></v-divider>
      <h5 class="headline mt-8">
        <span>Change Password</span>
      </h5>

      <div class="w-1/2 pr-3">
        <v-text-field
          v-model="changePasswordForm.new_password"
          flat
          solo
          required
          class="mt-4"
          type="password"
          placeholder="New Password"
          background-color="grey lighten-3"
          :error-messages="changePasswordForm.$getError('new_password')"
        ></v-text-field>
        <v-text-field
          v-model="changePasswordForm.new_password_confirmation"
          flat
          solo
          required
          class="mt-2"
          type="password"
          placeholder="Confirm New Password"
          background-color="grey lighten-3"
          :error-messages="
            changePasswordForm.$getError('new_password_confirmation')
          "
        ></v-text-field>
      </div>

      <v-btn
        color="primary"
        depressed
        :loading="changePasswordForm.$busy"
        @click="changePassword"
        class="mr-4"
        type="submit"
      >
        Update password
      </v-btn>
    </div>

    <div v-if="form" class="mt-8 mb-12 pa-0 w-3/4" style="margin-bottom: 50px">
      <v-divider></v-divider>
      <h5 class="headline mt-8">
        <span>Delete Account</span>
      </h5>
      <v-alert color="red" text class="mt-3">
        This action is not reversible. All information will be deleted
        immediately.
      </v-alert>
      <v-btn
        color="primary"
        depressed
        :loading="deleting"
        @click="deleteAccount"
        class="mr-4"
        type="submit"
      >
        Delete Account
      </v-btn>
    </div>

    <v-snackbar v-model="snackbar.show" right>
      {{ snackbar.message }}
      <v-btn color="pink" text @click="snackbar.show = false">Ok</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import {
  mdiChevronRight,
  mdiCheckCircle,
  mdiAlertCircleOutline,
  mdiCloseCircle,
  mdiAccountAlert,
  mdiAccountCheck,
  mdiAccountOff,
  mdiSettings
} from '@mdi/js'
import { mapState, mapActions, mapMutations } from 'vuex'
import AvatarForm from './components/AvatarForm'
import Form from '@/utils/form'

export default {
  name: 'UserDetails',

  components: { AvatarForm },

  data() {
    return {
      menu: false,
      icons: {
        arrow: mdiChevronRight,
        check: mdiCheckCircle,
        close: mdiCloseCircle,
        alert: mdiAlertCircleOutline,
        accountAlert: mdiAccountAlert,
        accountCheck: mdiAccountCheck,
        accountCancel: mdiAccountOff,
        settings: mdiSettings
      },
      loading: false,
      deleting: false,
      avatarLoading: false,
      form: null,
      changePasswordForm: new Form({
        new_password: null,
        new_password_confirmation: null
      }),
      showCropperModal: false,
      avatarTimestamp: new Date().getTime(),
      snackbar: {
        show: false,
        message: null
      }
    }
  },

  computed: {
    ...mapState({
      user: state => state.user.userDetails,
      authUser: state => state.auth.user
    }),
    breadCrumbItems() {
      return [
        {
          text: 'Users',
          link: true,
          to: { name: `users` }
        },
        {
          text: this.user ? this.user.full_name : ''
        }
      ]
    }
  },

  methods: {
    ...mapActions({
      getUserDetails: 'user/getUserDetails',
      changeAvatar: 'user/changeAvatar',
      updateUser: 'user/updateUser',
      deleteUser: 'user/deleteUser',
      rejectUser: 'user/rejectUser',
      verifyUser: 'user/verifyUser'
    }),

    ...mapMutations({
      clearUserDetails: 'user/clearUserDetails',
      setSelectedUserAvatar: 'user/setSelectedUserAvatar',
      userAvatarRemoved: 'user/userAvatarRemoved'
    }),

    async unverify() {
      const result = await this.$swal({
        title: 'Unverifying User',
        text: 'Are you sure you want to unverify this user?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Continue',
        showLoaderOnConfirm: true,
        allowOutsideClick: () => !this.$swal.isLoading(),
        preConfirm: async val => {
          try {
            await this.$api.put('users/' + this.user.id + '/unverify')
            await this.getUserDetails(this.$route.params.id)
            this.form = new Form(this.user)
          } catch (e) {
            this.$swal.showValidationMessage(
              'Unable to unverify user. Please try again later.'
            )
          }
        }
      })

      if (result.isConfirmed) {
        this.showSnackbar('User successfully  unverified!')
      }
    },

    async changePassword() {
      try {
        this.changePasswordForm.$busy = true
        await this.$api.post(
          'users/' + this.user.id + '/change-password',
          this.changePasswordForm.$data()
        )

        this.showSnackbar('Password successfully updated!')
        this.changePasswordForm.$reset()
      } catch (err) {
        if (err.response.status === 422) {
          this.changePasswordForm.$setErrors(err.response.data.errors)
        }
      } finally {
        this.changePasswordForm.$busy = false
      }
    },

    async deleteAccount() {
      const result = await this.$swal({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      })

      if (result.isConfirmed) {
        try {
          this.deleting = true
          await this.deleteUser(this.user)
          this.$router.replace({ name: 'users.all' })
        } finally {
          this.deleting = false
        }
      }
    },

    async getUser() {
      this.loading = true
      await this.getUserDetails(this.$route.params.id)
      this.form = new Form(this.user)
      this.loading = false
    },

    async avatarChange(image) {
      this.avatarLoading = true
      let data = new FormData()
      data.append('user_id', this.user.id)
      data.append('avatar', image)
      await this.changeAvatar(data)
      this.avatarLoading = false
      this.showSnackbar('Avatar successfully updated!')
    },

    async updateUserDetails() {
      this.form.$busy = true
      this.form.$clearErrors()
      this.updateUser({
        id: this.user.id,
        first_name: this.form.first_name,
        last_name: this.form.last_name
      })
        .then(() => {
          this.form.$busy = false
          this.showSnackbar('User details successfully updated!')
        })
        .catch(err => {
          this.form.$busy = false
          if (err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }
        })
    },

    async onAcceptVerification() {
      this.menu = false
      this.loading = true

      await this.verifyUser({ id: this.user.id })
        .then(() => {
          this.form.$busy = false
          this.showSnackbar('User successfully verified!')
        })
        .catch(err => {
          this.form.$busy = false
          if (err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }
        })

      this.loading = false
    },

    async onRejectVerification() {
      this.menu = false
      this.loading = true

      await this.rejectUser({
        id: this.user.id,
        rejection_message: this.form.rejection_message
      })
        .then(() => {
          this.showSnackbar('User successfully verified!')
        })
        .catch(err => {
          this.form.$busy = false
          if (err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }
        })

      this.loading = false
    },

    showSnackbar(message) {
      this.snackbar.message = message
      this.snackbar.show = true
    }
  },

  created() {
    this.getUser()
  },

  destroyed() {
    this.clearUserDetails()
  },

  watch: {
    $route() {
      this.getUser()
    }
  }
}
</script>

<style>
.skeleton-width {
  width: 400px;
}

.v-skeleton-loader__bone {
  padding: 0 !important;
}
</style>
